// EPC service × location pages — targets "EPC {town}" searches.

export const EPC_PRICING = {
  from: "£85",
  note: "Extensions to the main building, solar PV and rooms in the roof increase the price due to the additional documentation required — your fixed quote is confirmed before booking.",
};

export const EPC_TOWNS = [
  {
    slug: "coningsby",
    name: "Coningsby",
    areaSlug: "coningsby",
    metaDescription:
      "EPC in Coningsby from £85 — accredited domestic energy assessor serving Coningsby, Tattershall and RAF Coningsby rentals. Same-week appointments, certificate lodged on the national register.",
    intro:
      "With RAF Coningsby driving one of the busiest rental markets in mid-Lincolnshire, EPCs are in constant demand here — every new tenancy needs a valid certificate before it can legally begin.",
    paragraphs: [
      "Much of Coningsby's lettings stock turns over quickly as service families post in and out, and landlords often discover an expired EPC at exactly the wrong moment — with a tenant ready to move in. I offer same-week EPC appointments across Coningsby and Tattershall, with the certificate lodged on the national register promptly after the visit, so your letting isn't held up.",
      "For sellers and buyers, the village's mix of post-war ex-service housing, bungalows and newer developments generally assesses straightforwardly — though extensions and loft conversions need additional documentation, which is reflected in the quote. If your property is unusual, send me the details and I'll confirm a fixed price the same day.",
    ],
    faqs: [
      {
        q: "How much does an EPC cost in Coningsby?",
        a: "EPCs in Coningsby start from £85. Extensions to the main building, solar PV and rooms in the roof increase the price because of the additional documentation required — but your quote is fixed and confirmed before booking, with no surprises after the visit.",
      },
      {
        q: "How quickly can you do an EPC near RAF Coningsby?",
        a: "Same-week appointments are available for most Coningsby and Tattershall properties, and the certificate is typically lodged on the national register within days of the visit — so a new tenancy isn't delayed.",
      },
      {
        q: "Do I need a new EPC for every new tenancy?",
        a: "No — an EPC is valid for 10 years and covers successive tenancies within that period. But the property must meet the minimum energy standard (currently band E) before each new tenancy, so if your certificate is old or low-rated it's worth reviewing before you re-let.",
      },
    ],
  },
  {
    slug: "tattershall",
    name: "Tattershall",
    areaSlug: null,
    metaDescription:
      "EPC in Tattershall from £85 — accredited domestic energy assessor covering Tattershall, Tattershall Thorpe and Coningsby. Same-week appointments for sales and lettings.",
    intro:
      "From cottages in the shadow of Tattershall Castle to modern homes and the busy rental market shared with neighbouring Coningsby, every sale and letting in the village needs a valid EPC — and I can usually be there the same week.",
    paragraphs: [
      "Tattershall's older cottages and character properties often rate lower than owners expect — solid walls, older heating systems and limited insulation all pull the band down. Alongside the certificate itself, I'll tell you plainly which improvements would genuinely lift the rating and which aren't worth the money, something that matters increasingly as rental standards tighten.",
      "The village's lettings market — buoyed by RAF Coningsby across the river — means landlord EPCs are the most common request here, often alongside a photographed inspection report for the same visit. Coverage includes Tattershall Thorpe, Coningsby and the surrounding Fen-edge villages.",
    ],
    faqs: [
      {
        q: "How much does an EPC cost in Tattershall?",
        a: "From £85. Extensions, solar PV and rooms in the roof add to the price due to the extra documentation required — the quote is fixed and confirmed before booking.",
      },
      {
        q: "My Tattershall cottage is old — will it fail the EPC?",
        a: "An EPC can't be 'failed' — it simply records a band from A to G. For owner-occupied sales any band is fine. For lettings, the property currently needs at least a band E, and older solid-wall cottages sometimes fall below that — if yours does, I'll set out the most cost-effective route back above the line.",
      },
      {
        q: "Can you do the EPC and a landlord inspection in one visit?",
        a: "Yes — combining an EPC with a photographed landlord inspection report in a single appointment is common for Tattershall and Coningsby rentals, and saves on both cost and arranging access twice.",
      },
    ],
  },
  {
    slug: "woodhall-spa",
    name: "Woodhall Spa",
    areaSlug: "woodhall-spa",
    metaDescription:
      "EPC in Woodhall Spa from £85 — accredited energy assessor for the village's Edwardian villas, character homes and holiday lets. Same-week appointments, fixed quotes.",
    intro:
      "Woodhall Spa's distinctive Edwardian villas and character homes are wonderful to live in — and notoriously modest performers on an EPC, which makes accurate assessment and honest advice matter more here than almost anywhere in the county.",
    paragraphs: [
      "Older village properties — timber bays, early cavity or solid walls, original features owners rightly want to keep — often sit in bands D and E. The difference between a careful assessment and a rushed one can be a whole band: documentary evidence of insulation, glazing and heating upgrades lets me record the property as it actually is rather than assuming age-based defaults. Dig out your paperwork before the visit; it's the cheapest improvement there is.",
      "Woodhall Spa's healthy holiday-let and rental market adds the compliance angle: lettings need at least a band E today, with tighter standards proposed. For high-value character homes, I can pair the certificate with independent energy advice — a costed route to a better band that respects the building's character.",
    ],
    faqs: [
      {
        q: "How much does an EPC cost in Woodhall Spa?",
        a: "From £85 for most properties. Extensions to the main building, solar PV and rooms in the roof increase the price due to the additional documentation required — larger character villas with several extensions will be quoted accordingly, fixed before booking.",
      },
      {
        q: "Does a holiday let in Woodhall Spa need an EPC?",
        a: "In most cases where the property is let and occupied, yes — and listings on major platforms increasingly expect one. If you're unsure whether your arrangement requires a certificate, get in touch and I'll give you a straight answer.",
      },
      {
        q: "Will an EPC assessor mark my character home down for original features?",
        a: "The assessment records what's there — but evidence changes outcomes. Proof of underfloor or loft insulation, secondary glazing, modern heating controls and boiler documentation all lift the result. I'll also tell you which sympathetic improvements would raise the band without harming the character.",
      },
    ],
  },
  {
    slug: "horncastle",
    name: "Horncastle",
    areaSlug: "horncastle",
    metaDescription:
      "EPC in Horncastle from £85 — accredited domestic energy assessor for Horncastle's period homes, rentals and village properties. Same-week appointments, certificate lodged promptly.",
    intro:
      "Horncastle's Georgian core and solid-wall period homes make EPCs here genuinely consequential — for sellers, an honest rating; for landlords, the line between a lettable property and an unlawful one.",
    paragraphs: [
      "Period property dominates the town, and solid-wall homes with older heating are exactly the stock that lands in the lower bands. For landlords that matters legally: a rental needs at least a band E before a new tenancy, and the proposed move towards band C will bite hardest on towns like Horncastle. My advice alongside the certificate is practical and prioritised — controls, insulation and evidence first, expensive measures only where they're genuinely the fastest route.",
      "For sales, agents in and around Horncastle need the EPC in place before marketing begins — I offer same-week appointments across the town and surrounding villages, from Tetford and the Wolds down to Woodhall Spa and Coningsby, with the certificate lodged on the national register promptly after the visit.",
    ],
    faqs: [
      {
        q: "How much does an EPC cost in Horncastle?",
        a: "From £85. Extensions to the main building, solar PV and rooms in the roof increase the price due to the additional documentation required. Every quote is fixed and confirmed before booking.",
      },
      {
        q: "My Horncastle rental is rated F — can I still let it?",
        a: "Not lawfully, unless a valid exemption is registered. A band F or G property needs improvement to at least band E before a new tenancy. The route up is often cheaper than expected — heating controls, loft top-up and documented improvements frequently do it. I can assess, advise and re-lodge once works are done.",
      },
      {
        q: "Do you cover villages around Horncastle?",
        a: "Yes — the Wolds villages, Tetford, Woodhall Spa, Coningsby, Tattershall and the surrounding area are all covered, usually within the same week.",
      },
    ],
  },
  {
    slug: "boston",
    name: "Boston",
    areaSlug: "boston",
    metaDescription:
      "EPC in Boston, Lincolnshire from £85 — accredited domestic energy assessor for Boston's terraces, rentals and village properties. Same-week appointments, fixed quotes.",
    intro:
      "Boston has one of the largest private rental sectors in Lincolnshire — and a housing stock of solid-wall Victorian terraces that sits naturally in the lower EPC bands. That combination makes the town one of the busiest places in the county for energy assessments.",
    paragraphs: [
      "Central Boston's terraces — solid brick walls, suspended timber floors, older boilers — commonly assess at D or E, which puts many rentals close to the legal minimum. For landlords, an accurate assessment is the difference between compliance and an unlawful letting; where a property sits at the margin, documentary evidence of improvements and a handful of low-cost measures usually secures the band. I'll tell you exactly which.",
      "Coverage spans the whole borough: Wyberton, Kirton, Swineshead, Fishtoft, Sutterton and the surrounding Fen villages, with same-week appointments for most addresses. Agents and portfolio landlords can block-book multiple properties in a single day at sensible rates.",
    ],
    faqs: [
      {
        q: "How much does an EPC cost in Boston?",
        a: "From £85 per property. Extensions to the main building, solar PV and rooms in the roof increase the price due to the additional documentation required. Multiple properties booked together can usually be assessed in one day — ask about block bookings.",
      },
      {
        q: "Why do Boston terraces get low EPC ratings?",
        a: "Solid brick walls lose heat faster than insulated cavity walls, and the methodology reflects that — combined with older heating systems and limited loft insulation, many terraces land at D or E. The fastest improvements are usually heating controls, loft insulation top-ups and low-energy lighting, plus documented evidence of any past upgrades.",
      },
      {
        q: "I'm a landlord with several Boston properties — can you do them together?",
        a: "Yes — block bookings across multiple properties in one day are common for Boston landlords and agents, and keep the per-property cost down. Send the address list and I'll quote the day.",
      },
      {
        q: "How long does the EPC visit take?",
        a: "Typically 45 minutes to an hour for a standard terrace — photographs, measurements, heating and insulation details — and the certificate is lodged on the national register shortly after, valid for 10 years.",
      },
    ],
  },
  {
    slug: "lincoln",
    name: "Lincoln",
    areaSlug: "lincoln",
    metaDescription:
      "EPC in Lincoln from £85 — accredited domestic energy assessor for Lincoln's period homes, terraces, rentals and HMOs. Same-week appointments, certificate lodged promptly.",
    intro:
      "Between the university's rental market, uphill period homes that rate low by their nature, and a steady sales market, Lincoln generates more EPC demand than anywhere else in the county.",
    paragraphs: [
      "Lincoln's landlords — from single-property owners to HMO portfolios around the West End and Monks Road — need valid certificates before every new tenancy, and many of the city's older terraces sit close to the minimum standard. I provide accredited EPCs across the city with same-week appointments, and for properties at the margin I'll set out the cheapest documented route to a safer band.",
      "Uphill, solid-wall stone and brick homes rate modestly by construction — which makes documentary evidence of improvements (insulation, glazing, heating) worth gathering before the visit, as it can lift the result a full band. Coverage spans the whole city plus North Hykeham, Birchwood, Cherry Willingham and surrounding villages, with block bookings available for agents.",
    ],
    faqs: [
      {
        q: "How much does an EPC cost in Lincoln?",
        a: "From £85 per property. Extensions to the main building, solar PV and rooms in the roof increase the price due to the additional documentation required — quotes are fixed and confirmed before booking, and block bookings for agents and portfolio landlords keep per-property costs down.",
      },
      {
        q: "Do HMOs in Lincoln need an EPC?",
        a: "Where the whole property is let on a single tenancy, yes. Room-by-room lettings have historically sat in a grey area, but licensing authorities and proposed regulations increasingly expect one — and a valid EPC is never wasted. If you're unsure, describe the setup and I'll give you a straight answer.",
      },
      {
        q: "How quickly can you do an EPC in Lincoln?",
        a: "Same-week appointments are the norm, and the certificate is typically lodged on the national register within days — quick enough for marketing deadlines and tenancy start dates.",
      },
    ],
  },
  {
    slug: "grantham",
    name: "Grantham",
    areaSlug: "grantham",
    metaDescription:
      "EPC in Grantham from £85 — accredited domestic energy assessor for Grantham's sales, lettings and new build market. Same-week appointments, fixed quotes.",
    intro:
      "Grantham's fast-moving, commuter-driven sales market means agents need EPCs in place before marketing begins — and the town's mix of Victorian terraces and brand-new estates covers both ends of the rating scale.",
    paragraphs: [
      "For sellers, the certificate must exist before the listing goes live, and I offer same-week appointments across Grantham, Barrowby, Great Gonerby and the surrounding villages to keep marketing on schedule. New build owners selling on within ten years can usually rely on the original certificate — I'll check the register for you before you pay for one you don't need.",
      "The town's older terraces near the centre tell the other story: solid walls and older heating that land in bands D and E, where lettings compliance starts to bite. Alongside the certificate I'll identify the cheapest documented route upward — usually controls, loft top-ups and evidence of past improvements rather than major works.",
    ],
    faqs: [
      {
        q: "How much does an EPC cost in Grantham?",
        a: "From £85. Extensions to the main building, solar PV and rooms in the roof increase the price due to the additional documentation required — your fixed quote is confirmed before booking.",
      },
      {
        q: "My Grantham house is only a few years old — do I need a new EPC to sell?",
        a: "Probably not. EPCs last 10 years, and your builder lodged one at completion. Send me the address and I'll check the national register for free before you book anything.",
      },
      {
        q: "Can you do the EPC before our listing photos this week?",
        a: "Usually, yes — same-week appointments are standard in Grantham, and agents routinely book the EPC alongside the photography slot so the listing launches complete.",
      },
    ],
  },
  {
    slug: "sleaford",
    name: "Sleaford",
    areaSlug: "sleaford",
    metaDescription:
      "EPC in Sleaford from £85 — accredited domestic energy assessor for Sleaford's sales and RAF-linked rental market. Same-week appointments, fixed quotes, lodged promptly.",
    intro:
      "Sleaford's rental market — buoyed by RAF stations across mid-Lincolnshire — keeps landlord EPCs in constant demand, while the town's mix of period and modern stock spans the full rating scale.",
    paragraphs: [
      "Service-family tenancies turn over on posting cycles, and each new tenancy needs a valid certificate with at least a band E rating. I provide same-week EPCs across Sleaford, Ruskington, Heckington and the surrounding villages, with prompt lodging so lettings aren't delayed — and combined EPC-plus-inspection visits for landlords who want both boxes ticked at once.",
      "For sales, the town-centre period stock rates modestly while the newer estates to the north and west assess well; either way, documentary evidence of improvements gathered before the visit is the cheapest band-lifter there is. Agents can block-book multiple properties into a single day.",
    ],
    faqs: [
      {
        q: "How much does an EPC cost in Sleaford?",
        a: "From £85 per property, with extensions, solar PV and rooms in the roof priced slightly higher due to the additional documentation required. Fixed quotes, confirmed same day.",
      },
      {
        q: "Can you combine the EPC with a landlord inspection report?",
        a: "Yes — one visit, both documents. It's the most common booking for Sleaford rentals, and it saves arranging access twice between tenancies.",
      },
      {
        q: "Do you cover the villages around Sleaford?",
        a: "Yes — Ruskington, Heckington, Billinghay and the wider district are all covered, usually within the same week.",
      },
    ],
  },
  {
    slug: "spalding",
    name: "Spalding",
    areaSlug: "spalding",
    metaDescription:
      "EPC in Spalding from £85 — accredited domestic energy assessor for Spalding, Holbeach, Pinchbeck and South Holland. Same-week appointments, fixed quotes.",
    intro:
      "Between South Holland's busy rental sector, a strong bungalow market and some of the county's fastest-growing new estates, Spalding keeps an energy assessor moving — and every sale and letting needs its certificate.",
    paragraphs: [
      "Spalding's post-war bungalows are an EPC staple: generally straightforward to assess, often sitting mid-table, and frequently improvable for very little — loft top-ups and heating controls go a long way on a single-storey footprint. The town's rental stock adds the compliance angle, with band E the current legal floor and tighter standards proposed.",
      "Coverage spans the whole of South Holland — Holbeach, Pinchbeck, Sutton Bridge, Crowland and the Fen villages — with same-week appointments and prompt lodging on the national register. Landlords and agents with multiple properties can block-book a day at sensible rates.",
    ],
    faqs: [
      {
        q: "How much does an EPC cost in Spalding?",
        a: "From £85. Extensions to the main building, solar PV and rooms in the roof increase the price due to the additional documentation required — quotes are fixed before booking.",
      },
      {
        q: "What's the quickest way to lift a bungalow's EPC rating?",
        a: "On most Spalding bungalows: top the loft insulation up to 270mm+, fit a full set of heating controls, switch to low-energy lighting throughout, and show documentary evidence of any past improvements. Modest money, reliable results.",
      },
      {
        q: "Do you cover Holbeach and Sutton Bridge?",
        a: "Yes — all of South Holland is covered, including Holbeach, Pinchbeck, Sutton Bridge and Crowland, usually within the week.",
      },
    ],
  },
  {
    slug: "louth",
    name: "Louth",
    areaSlug: "louth",
    metaDescription:
      "EPC in Louth from £85 — accredited domestic energy assessor for Louth's period homes, Wolds villages and holiday lets. Honest advice for character properties.",
    intro:
      "Louth's beautiful period stock has a predictable EPC problem: solid walls, original windows and character features that the methodology rates modestly — which makes accurate assessment and honest advice matter here more than most places.",
    paragraphs: [
      "Georgian townhouses and Wolds cottages routinely assess at D and E — and for the town's healthy rental and holiday-let market, that puts some properties near the legal floor. The answer is rarely drastic: documentary evidence of improvements, heating controls, loft insulation and sympathetic measures usually secure the band without touching the building's character. I'll tell you plainly which improvements suit an old building and which would do more harm than good.",
      "Coverage includes the Wolds villages, Alford, Spilsby and towards the coast, with same-week appointments for most addresses and the certificate lodged promptly on the national register.",
    ],
    faqs: [
      {
        q: "How much does an EPC cost in Louth?",
        a: "From £85, with extensions, solar PV and rooms in the roof priced slightly higher due to the additional documentation required. Every quote is fixed and confirmed before booking.",
      },
      {
        q: "Will improving my period home's EPC ruin its character?",
        a: "It doesn't have to. Controls, loft insulation, and evidence-gathering lift ratings without touching the fabric; measures like external wall insulation are where character gets harmed, and they're rarely the right answer in Louth. I advise with the building's character in mind.",
      },
      {
        q: "Does my Louth holiday cottage need an EPC?",
        a: "In most letting arrangements, yes — and booking platforms increasingly expect one. If you're unsure whether yours qualifies, describe the setup and I'll give you a straight answer.",
      },
    ],
  },
  {
    slug: "skegness",
    name: "Skegness",
    areaSlug: "skegness",
    metaDescription:
      "EPC in Skegness from £85 — accredited domestic energy assessor for coastal homes, holiday lets and rentals in Skegness, Ingoldmells and Chapel St Leonards.",
    intro:
      "Between holiday lets, seasonal rentals and a year-round lettings market, the Skegness coast runs on certificates — and coastal properties bring their own assessment quirks.",
    paragraphs: [
      "Holiday lets and seasonal rentals across Skegness, Ingoldmells and Chapel St Leonards increasingly need valid EPCs for compliance and platform listings, while the permanent rental market answers to the band E minimum like everywhere else. I offer same-week appointments along the coast, with certificates lodged promptly so changeovers and new tenancies aren't held up.",
      "Coastal stock — bungalows, chalets and converted properties — varies widely in construction, which is exactly where an experienced assessor earns the fee: recording construction types correctly rather than defaulting to worst-case assumptions, and advising on the handful of measures that genuinely lift a coastal property's band.",
    ],
    faqs: [
      {
        q: "How much does an EPC cost in Skegness?",
        a: "From £85 per property. Extensions to the main building, solar PV and rooms in the roof increase the price due to the additional documentation required — fixed quotes confirmed before booking.",
      },
      {
        q: "Does my Skegness holiday let or chalet need an EPC?",
        a: "Most let-and-occupied holiday properties do, and listing platforms increasingly require one regardless. Chalets and park properties vary case by case — describe yours and I'll give you a straight answer before you book anything.",
      },
      {
        q: "Do you cover Ingoldmells and Chapel St Leonards?",
        a: "Yes — the whole coastal strip is covered, including Ingoldmells, Chapel St Leonards and the villages inland, usually within the week.",
      },
    ],
  },
];

export const getEpcTown = (slug) => EPC_TOWNS.find((t) => t.slug === slug);
